import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import Footer from './Footer';

const HomePage = () => {
  const { signInWithGoogle, user, loading } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  // Removed automatic redirect to dashboard - let users stay on home page

  return (
    <div className="font-sans text-slate-800 antialiased" style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}>
      <style>{`
        :root { 
          --brand-900: #0f172a; 
          --muted: #64748b; 
        }
        body { 
          background-color: #f8fafc; 
          color: var(--brand-900); 
        }
        h1,h2,h3,h4 { 
          color: var(--brand-900); 
        }
        .rounded-image { 
          border-radius: 12px; 
        }
        .site-container { 
          max-width: 1100px; 
          margin-left: auto; 
          margin-right: auto; 
        }
      `}</style>

      <main>
        {/* Hero */}
        <section className="py-16 bg-white">
          <div className="site-container px-6">
            <div className="grid grid-cols-1 gap-y-12 md:grid-cols-2 md:items-center md:gap-x-12 lg:gap-x-20">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-slate-900">
                  Begin your tracking journey
                </h1>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Every data point tells a story. Start capturing your life's narrative with our intuitive tracking platform.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/track')}
                    className="inline-flex items-center rounded-md bg-slate-900 px-8 py-3 text-white shadow hover:bg-slate-800 transition-colors"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center rounded-md border border-slate-300 px-8 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Explore
                  </button>
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <img
                  src="image2_1.jpg"
                  className="w-full max-w-md rounded-lg object-cover shadow-lg"
                  alt="Life tracking overview"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-slate-50">
          <div className="site-container px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need to Track Your Life</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Comprehensive tracking tools designed for modern professionals who want to optimize every aspect of their daily routine.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Real-time Data</h3>
                <p className="text-slate-600">Live updates so you can react instantly to market changes, news, and personal metrics.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">AI Analytics</h3>
                <p className="text-slate-600">Actionable insights powered by AI to help you make better decisions and track progress.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Cross-platform</h3>
                <p className="text-slate-600">Access your data anywhere, anytime with seamless synchronization across all devices.</p>
              </div>
            </div>

            {/* Trackers Grid */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Available Trackers</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Stocks</h4>
                  <p className="text-sm text-slate-600">Market monitoring</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">News</h4>
                  <p className="text-sm text-slate-600">Latest headlines</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Fitness</h4>
                  <p className="text-sm text-slate-600">Workout tracking</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Food</h4>
                  <p className="text-sm text-slate-600">Nutrition tracking</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Sleep</h4>
                  <p className="text-sm text-slate-600">Rest analysis</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Books</h4>
                  <p className="text-sm text-slate-600">Reading list</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Tasks</h4>
                  <p className="text-sm text-slate-600">Productivity</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
                  <h4 className="font-semibold text-slate-900 mb-1">Notes</h4>
                  <p className="text-sm text-slate-600">Quick notes</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-slate-900 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Your Journey?</h3>
              <p className="text-slate-200 mb-6 max-w-2xl mx-auto">
                Start building your personalized tracking system today and discover the power of data-driven self-improvement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/track')}
                  className="inline-flex items-center rounded-md bg-white px-8 py-3 text-slate-900 shadow hover:bg-slate-100 transition-colors font-semibold"
                >
                  Start Tracking Now
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center rounded-md border border-slate-300 px-8 py-3 text-white hover:bg-slate-800 transition-colors"
                >
                  View Dashboard
                </button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default HomePage;